import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMenuByKitchenId, getKitchenDetails } from "../services/kitchen.service"; // ✅ Import from service
import { Menu, MenuItem, CartItem, Kitchen, RadialSelection } from "../types/index";
import { useCartStore } from "../store/cart.store";
import RadialMenu from "../components/RadialMenu";
import CircleLogo from "../components/CircleLogo";
import { Button } from "../components/common/Button";
import { toast } from "react-hot-toast";
import { parseAndValidatePrice, hasValidPrice } from '../utils/priceValidator';

// ✅ REMOVED: getKitchenDetails function (now in service)

interface PriceInfo {
  basePrice: number;
  variantPrice: number | null;
  sizePrice: number | null;
  selectedPrice: number;
}

/**
 * Calculate price from variants/sizes with full validation
 */
const calculateItemPrice = (item: MenuItem): PriceInfo => {
  const basePrice = parseAndValidatePrice(item.price);
  
  let variantPrice: number | null = null;
  let sizePrice: number | null = null;

  // Get first variant price if available
  if (item.Variants && item.Variants.length > 0) {
    const firstVariant = item.Variants[0];
    variantPrice = parseAndValidatePrice(firstVariant.price);
  }

  // Get first size price if available
  if (item.Sizes && item.Sizes.length > 0) {
    const firstSize = item.Sizes[0];
    sizePrice = parseAndValidatePrice(firstSize.price);
  }

  // Priority: variant > size > base > 0
  const selectedPrice = variantPrice ?? sizePrice ?? basePrice ?? 0;

  return { basePrice, variantPrice, sizePrice, selectedPrice };
};

/**
 * Main KitchenMenu Component
 */
const KitchenMenu: React.FC = () => {
  const { kitchenId = "" } = useParams<{ kitchenId: string }>();
  const [viewMode, setViewMode] = useState<"standard" | "radial">("radial");
  const [pendingSelection, setPendingSelection] = useState<RadialSelection | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedSides, setSelectedSides] = useState<string[]>([]);
  const [selectedSpecials, setSelectedSpecials] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const { items: cart, addItem: addToCart, totalPrice } = useCartStore();

  // ✅ Fetch menu with improved config
  const { 
    data: menu, 
    isLoading: isMenuLoading, 
    isError: isMenuError,
    error: menuError 
  } = useQuery<Menu, Error>(
    ["menu", kitchenId],
    () => getMenuByKitchenId(kitchenId),
    { 
      enabled: !!kitchenId,
      retry: 2, // ✅ Only retry twice
      retryDelay: 1000,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // ✅ Fetch kitchen details with improved config
  const { 
    data: kitchenDetails,
    isLoading: isKitchenLoading 
  } = useQuery<Kitchen | null, Error>(
    ["kitchenDetails", kitchenId],
    () => getKitchenDetails(kitchenId),
    { 
      enabled: !!kitchenId,
      retry: 2,
      retryDelay: 1000,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Build validated menu items array
  const menuItemsArray = useMemo(() => {
    if (!menu?.menuItems) return [];

    return Object.entries(menu.menuItems)
      .map(([key, data]) => {
        const itemData = data as any;
        
        // ✅ CRITICAL VALIDATION: Ensure item has valid pricing
        const priceInfo = calculateItemPrice(itemData);
        
        if (priceInfo.selectedPrice === 0 && !itemData.price && !itemData.Variants?.length && !itemData.Sizes?.length) {
          console.error(`❌ Item "${itemData.name || key}" has no valid price source. Skipping.`, itemData);
          return null;
        }

        return { 
          key, 
          data: itemData,
          priceInfo 
        };
      })
      .filter(
        (item): item is { key: string; data: any; priceInfo: PriceInfo } =>
          item !== null && item.data.available !== false
      );
  }, [menu]);

  // Handlers
  const handleRadialSelect = (selection: RadialSelection) => {
    setPendingSelection(selection);
    setSelectedAddons([]);
    setSelectedSides([]);
    setSelectedSpecials([]);
    setQuantity(1);
  };

  /**
   * Add item to cart with FULL price validation
   */
  const addToCartFromSelection = (
    selection: RadialSelection,
    addons: string[],
    sides: string[],
    specials: string[],
    qty: number
  ) => {
    try {
      const { item, variant, size, price: selectionPrice } = selection;

      // ✅ FIX #1: Validate base price from selection
      let basePrice = parseAndValidatePrice(selectionPrice);
      
      // Fallback: get from item if selection price is missing
      if (basePrice === 0 && item.price) {
        basePrice = parseAndValidatePrice(item.price);
      }

      // ✅ FIX #2: If still no valid price, try variants/sizes
      if (basePrice === 0) {
        if (item.Variants && item.Variants[0]?.price) {
          basePrice = parseAndValidatePrice(item.Variants[0].price);
        } else if (item.Sizes && item.Sizes[0]?.price) {
          basePrice = parseAndValidatePrice(item.Sizes[0].price);
        }
      }

      // ✅ FIX #3: If we still have no price, throw error instead of silently failing
      if (basePrice === 0) {
        console.error("❌ FATAL: Item has no valid price", { item, variant, size });
        toast.error(`Cannot add "${item.name}" - price information is missing`);
        return;
      }

      let currentTotal = basePrice;

      // ✅ FIX #4: Add extras with price validation
      const addExtraPrice = (list: string[], source: any[] | undefined) => {
        if (!source || !Array.isArray(source)) return;

        list.forEach((name) => {
          const found = source.find(
            (opt: any) => (typeof opt === "string" ? opt : opt.name) === name
          );

          if (found && typeof found !== "string" && found.price) {
            const extraPrice = parseAndValidatePrice(found.price);
            if (extraPrice > 0) {
              currentTotal += extraPrice;
            }
          }
        });
      };

      addExtraPrice(addons, item.Addons);
      addExtraPrice(sides, item.Sides);
      addExtraPrice(specials, item.Specials);

      // ✅ FIX #5: Final validation - ensure total is a valid number
      if (isNaN(currentTotal) || currentTotal < 0) {
        console.error("❌ FATAL: Calculated invalid total", { currentTotal, basePrice });
        toast.error("Price calculation error. Please try again.");
        return;
      }

      // ✅ FIX #6: Generate deterministic ID
      const extrasId = [...addons, ...sides, ...specials].sort().join("|");
      const cleanItemId = (item.id || item.name).replace(/[^a-zA-Z0-9]/g, "");
      const cleanVariant = (variant || "base").replace(/[^a-zA-Z0-9]/g, "");
      const cleanSize = (size || "std").replace(/[^a-zA-Z0-9]/g, "");
      const deterministicId = `${kitchenId}_${cleanItemId}_${cleanVariant}_${cleanSize}_[${extrasId}]`;

      const itemToAdd: CartItem = {
        id: deterministicId,
        name: item.name,
        price: currentTotal, // ✅ GUARANTEED valid number
        quantity: qty,
        category: menu?.name || "Custom",
        kitchenId: kitchenId,
        kitchenName: menu?.name || kitchenId,
        photoUrl: item.photoUrl,
        description: item.description,
        customizations: {
          variant: variant,
          size: size,
          addons: addons,
          sides: sides,
          specials: specials,
        },
      };

      // ✅ FIX #7: Final type check before adding
      if (typeof itemToAdd.price !== "number" || isNaN(itemToAdd.price)) {
        throw new Error(`Invalid price for cart item: ${itemToAdd.name}`);
      }

      addToCart(itemToAdd);
      setPendingSelection(null);
      toast.success(`${item.name} added to cart!`);
    } catch (error) {
      console.error("❌ Error adding to cart:", error);
      toast.error("Failed to add item. Please try again.");
    }
  };

  const toggleOption = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    name: string
  ) => {
    setList((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]
    );
  };

  /**
   * Calculate modal total with validation
   */
  const currentModalTotal = useMemo(() => {
    if (!pendingSelection) return 0;

    let base = parseAndValidatePrice(
      pendingSelection.price || pendingSelection.item.price
    );

    const addExtras = (selected: string[], options: any[] | undefined) => {
      if (!options || !Array.isArray(options)) return;

      selected.forEach((name) => {
        const opt = options.find(
          (o) => (typeof o === "string" ? o : o.name) === name
        );
        if (opt && typeof opt !== "string" && opt.price) {
          base += parseAndValidatePrice(opt.price);
        }
      });
    };

    addExtras(selectedAddons, pendingSelection.item.Addons);
    addExtras(selectedSides, pendingSelection.item.Sides);
    addExtras(selectedSpecials, pendingSelection.item.Specials);

    const total = base * quantity;
    
    // ✅ Ensure we never return NaN
    return isNaN(total) ? 0 : total;
  }, [pendingSelection, selectedAddons, selectedSides, selectedSpecials, quantity]);

  const getItemDisplayName = (key: string, data: MenuItem) => {
    const d = data as any;
    return d.Base || d.name || key.replace(/^MYO?_/, "").replace(/_/g, " ");
  };

  const getBasePrice = (data: MenuItem) => {
    const priceInfo = calculateItemPrice(data);
    return priceInfo.selectedPrice;
  };

  const handleStandardItemClick = (key: string, data: MenuItem) => {
    const d = data as any;
    const priceInfo = calculateItemPrice(d);

    let variant = undefined;
    let size = undefined;

    if (d.Variants && d.Variants.length > 0) {
      variant = d.Variants[0].name;
    }
    if (d.Sizes && d.Sizes.length > 0) {
      size = d.Sizes[0].name;
    }

    handleRadialSelect({
      item: data,
      variant,
      size,
      price: priceInfo.selectedPrice, // ✅ Guaranteed valid price
    });
  };

  const resolvedLogo = kitchenDetails?.logoUrl || "/food-hero.svg";
  const resolvedName = kitchenDetails?.kitchen_name || menu?.name || "Menu";

  // ✅ IMPROVED LOADING STATES
  if (isMenuLoading || isKitchenLoading) {
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-orange-600 border-t-transparent mb-6"></div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Menu...</h3>
        <p className="text-sm text-gray-600 mb-4">
          Please wait while we fetch the latest dishes
        </p>
        <p className="text-xs text-gray-400">
          If this takes too long, please check:
        </p>
        <ul className="text-xs text-gray-500 space-y-1 mt-2">
          <li>• Your internet connection</li>
          <li>• Ad blocker settings (disable for this site)</li>
        </ul>
      </div>
    );
  }

  // ✅ IMPROVED ERROR STATE
  if (isMenuError) {
    const errorMessage = (menuError as Error)?.message || 'Unknown error';
    const isTimeout = errorMessage.includes('timeout');
    const isBlocked = errorMessage.includes('blocked') || errorMessage.includes('ad blocker');
    
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
          <div className="text-6xl mb-4">
            {isTimeout ? '⏱️' : isBlocked ? '🚫' : '⚠️'}
          </div>
          
          <h3 className="text-2xl font-bold text-red-800 mb-3">
            {isTimeout ? 'Request Timed Out' : isBlocked ? 'Content Blocked' : 'Failed to Load Menu'}
          </h3>
          
          <p className="text-red-700 mb-4 leading-relaxed">
            {errorMessage}
          </p>
          
          <div className="bg-white rounded-lg p-4 mb-6 text-left">
            <p className="font-semibold text-gray-800 mb-2">💡 Try these solutions:</p>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">1.</span>
                <span>Disable ad blockers or privacy extensions for this site</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">2.</span>
                <span>Check your internet connection</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">3.</span>
                <span>Clear browser cache and reload</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">4.</span>
                <span>Try using a different browser</span>
              </li>
            </ul>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition shadow-md"
            >
              🔄 Try Again
            </button>
            <button
              onClick={() => navigate('/home')}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ IMPROVED EMPTY STATE
  if (menuItemsArray.length === 0) {
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8 shadow-lg">
          <div className="text-6xl mb-4">🍽️</div>
          
          <h3 className="text-2xl font-bold text-yellow-800 mb-3">
            No Menu Items Available
          </h3>
          
          <p className="text-yellow-700 mb-6 leading-relaxed">
            This kitchen hasn't added any menu items yet, or all items are currently unavailable.
          </p>
          
          <div className="bg-white rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              <strong className="text-orange-600">{resolvedName}</strong> might be updating their menu. 
              Please check back later or browse other kitchens.
            </p>
          </div>
          
          <button
            onClick={() => navigate('/home')}
            className="bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition shadow-md w-full"
          >
            Browse Other Kitchens →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 pb-24">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-orange-700 mb-2">{resolvedName}</h2>
        <p className="text-gray-600 mb-4">Select an item to customize</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => setViewMode("standard")}
            className={`px-6 py-2 rounded-lg font-semibold transition ${
              viewMode === "standard"
                ? "bg-orange-600 text-white"
                : "bg-orange-100 text-orange-700"
            }`}
          >
            Table View
          </button>
          <button
            onClick={() => setViewMode("radial")}
            className={`px-6 py-2 rounded-lg font-semibold transition ${
              viewMode === "radial"
                ? "bg-orange-600 text-white"
                : "bg-orange-100 text-orange-700"
            }`}
          >
            Radial Menu ✨
          </button>
        </div>
      </div>

      {/* Radial or Table View */}
      {viewMode === "radial" ? (
        <RadialMenu
          kitchenId={kitchenId}
          kitchenName={resolvedName}
          kitchenLogo={resolvedLogo}
          menuItems={menuItemsArray.map((item) => ({
            key: item.key,
            data: item.data,
          }))}
          onItemSelect={handleRadialSelect}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-orange-600 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">Item</th>
                  <th className="py-4 px-6 text-right font-semibold">Price</th>
                  <th className="py-4 px-6 text-center font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {menuItemsArray.map((item) => (
                  <tr
                    key={item.key}
                    className="border-b border-orange-100 hover:bg-orange-50 transition"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-4">
                        <CircleLogo
                          src={item.data.photoUrl || "/food-hero.svg"}
                          alt={getItemDisplayName(item.key, item.data)}
                          size={48}
                          borderColor="#fdba74"
                        />
                        <div>
                          <div className="font-bold text-gray-800">
                            {getItemDisplayName(item.key, item.data)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.data.description || "No description"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right text-orange-700 font-bold">
                      ₹{getBasePrice(item.data).toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => handleStandardItemClick(item.key, item.data)}
                        className="px-4 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 transition"
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- CONFIRMATION MODAL --- */}
      {pendingSelection && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-orange-50 p-6 border-b border-orange-100 text-center shrink-0">
              <h4 className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-2">
                Confirm Selection
              </h4>
              <h3 className="text-xl font-bold text-gray-900">
                {pendingSelection.item.name}
              </h3>
              <div className="text-sm text-gray-600 mt-1 font-medium">
                {pendingSelection.variant && <span>{pendingSelection.variant} </span>}
                {pendingSelection.size && <span>• {pendingSelection.size}</span>}
              </div>
            </div>

            {/* Modal Body - Customization Options */}
            <div className="p-6 space-y-6 overflow-y-auto">
              {[
                {
                  title: "Extras",
                  items: pendingSelection.item.Addons,
                  selected: selectedAddons,
                  setter: setSelectedAddons,
                },
                {
                  title: "Sides",
                  items: pendingSelection.item.Sides,
                  selected: selectedSides,
                  setter: setSelectedSides,
                },
                {
                  title: "Specials",
                  items: pendingSelection.item.Specials,
                  selected: selectedSpecials,
                  setter: setSelectedSpecials,
                },
              ].map((group, groupIdx) =>
                group.items && group.items.length > 0 ? (
                  <div key={groupIdx}>
                    <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wider">
                      {group.title}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {group.items.map((opt: any, idx: number) => {
                        const name = typeof opt === "string" ? opt : opt.name;
                        const optPrice = parseAndValidatePrice(
                          typeof opt === "string" ? 0 : opt.price
                        );
                        const isSelected = group.selected.includes(name);

                        return (
                          <div
                            key={idx}
                            onClick={() => toggleOption(group.selected, group.setter, name)}
                            className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? "border-orange-500 bg-orange-50"
                                : "border-gray-200 hover:border-orange-300"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-5 h-5 rounded flex items-center justify-center border ${
                                  isSelected
                                    ? "bg-orange-500 border-orange-500"
                                    : "border-gray-400 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <span className="text-white text-xs">✓</span>
                                )}
                              </div>
                              <span
                                className={`font-medium text-sm ${
                                  isSelected ? "text-orange-900" : "text-gray-700"
                                }`}
                              >
                                {name}
                              </span>
                            </div>
                            {optPrice > 0 && (
                              <span className="text-xs font-bold text-orange-600">
                                +₹{optPrice.toFixed(2)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              )}

              {/* Quantity Selector */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-700">Quantity</span>
                  <div className="flex items-center gap-4 bg-white rounded-full border border-gray-300 px-2 py-1 shadow-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 font-bold text-lg transition"
                    >
                      −
                    </button>
                    <span className="font-bold text-lg w-6 text-center text-orange-700">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 font-bold text-lg transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-white shrink-0 flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setPendingSelection(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={() =>
                  addToCartFromSelection(
                    pendingSelection,
                    selectedAddons,
                    selectedSides,
                    selectedSpecials,
                    quantity
                  )
                }
              >
                Add to Cart - ₹{currentModalTotal.toFixed(2)}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Cart Summary Bar */}
      {cart.length > 0 && !pendingSelection && (
        <div className="fixed bottom-6 left-4 right-4 max-w-md mx-auto z-40">
          <div
            className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-4 rounded-xl shadow-2xl flex justify-between items-center cursor-pointer hover:shadow-3xl transition"
            onClick={() => navigate("/cart")}
          >
            <div className="flex flex-col">
              <span className="font-bold text-lg">{cart.length} items</span>
              <span className="text-sm text-gray-400">
                ₹{totalPrice().toFixed(2)} total
              </span>
            </div>
            <div className="flex items-center gap-2 font-bold text-orange-400">
              View Cart <span>→</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitchenMenu;
